/**
 * 語音管家（#24 Phase C2）· route ee_voice_command。
 * 大麥克風按鈕 → Web Speech STT → NLU → dispatch 3 use case → TTS 朗讀 + 畫面顯示。
 * 不支援語音的瀏覽器退回文字輸入（graceful）。
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { useSpeech } from '../hooks/useSpeech';
import FunctionPageHeader from '../components/FunctionPageHeader';
import {
  dispatchNlu, runIntent,
  checkMicPermission, requestMicPermission, detectMicGuide,
  type MicPermission,
} from '../services/voiceService';

const FUNC = 'ee_voice_command';

const VoicePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showApiError } = useMessage();
  const { supported, listening, startListening, speak } = useSpeech(i18n.language || 'zh-TW');

  const canRead = hasPermission(FUNC, 'read');
  const [heard, setHeard] = useState('');
  const [answer, setAnswer] = useState('');
  const [textInput, setTextInput] = useState('');
  const [busy, setBusy] = useState(false);
  // 麥克風權限識別（porsche 2026-08-19）：mount 偵測 + 監聽變化（reactive）
  const [micPerm, setMicPerm] = useState<MicPermission | 'unknown'>('unknown');
  const [permModalOpen, setPermModalOpen] = useState(false);

  useEffect(() => {
    let status: PermissionStatus | null = null;
    let cancelled = false;
    (async () => {
      const p = await checkMicPermission();
      if (!cancelled) setMicPerm(p);
      // 若支援 Permissions API，掛 onchange → 使用者在瀏覽器改設定後畫面即時反映
      try {
        if (navigator.permissions?.query) {
          status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          status.onchange = () => setMicPerm(status!.state as MicPermission);
        }
      } catch {
        // silent ok: Safari 舊版無 Permissions API，偵測失敗不影響文字/語音 fallback
      }
    })();
    return () => { cancelled = true; if (status) status.onchange = null; };
  }, []);

  const process = async (utterance: string) => {
    if (!utterance.trim()) return;
    setHeard(utterance); setAnswer(''); setBusy(true);
    try {
      const nlu = await dispatchNlu(utterance);
      const result = await runIntent(nlu);
      const text = result?.text_for_tts || t('voice.notUnderstood', '抱歉，我沒聽懂，請再說一次');
      setAnswer(text);
      speak(text);
    } catch (err) { showApiError(err); }
    finally { setBusy(false); }
  };

  const beginListening = () =>
    startListening(process, () => showApiError({ message: t('voice.noSupport', '') }));

  // 權限感知的麥克風點擊：granted→直接聽；prompt/unknown→請求授權（彈窗）；denied/unsupported→顯示指引
  const onMic = async () => {
    if (micPerm === 'granted') { beginListening(); return; }
    if (micPerm === 'denied' || micPerm === 'unsupported') { setPermModalOpen(true); return; }
    // prompt / unknown → 觸發瀏覽器授權彈窗
    const ok = await requestMicPermission();
    if (ok) { setMicPerm('granted'); beginListening(); }
    else { setMicPerm('denied'); setPermModalOpen(true); }
  };

  // Modal 「重試」：使用者到瀏覽器改完設定後再次請求；成功即關窗並開始聆聽（不 reload）
  const onRetry = async () => {
    const ok = await requestMicPermission();
    if (ok) { setMicPerm('granted'); setPermModalOpen(false); beginListening(); }
    else { setMicPerm('denied'); }   // 仍被拒 → 保持 modal 開著讓使用者續設定
  };

  if (permLoading) return null;
  if (!canRead) return (
    <div><FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: 24, color: '#d32f2f' }}>{t('common.noPermission', '您沒有權限存取此功能')}</div></div>
  );

  const mic: React.CSSProperties = {
    width: 120, height: 120, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 48,
    color: '#fff', background: listening ? '#d32f2f' : '#06c755',
    boxShadow: listening ? '0 0 0 8px rgba(211,47,47,0.2)' : '0 4px 12px rgba(0,0,0,0.15)',
  };

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: 24, maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        {supported ? (
          <>
            <button style={mic} disabled={busy} onClick={onMic} aria-label={t('voice.tapToSpeak', '按下說話')}>🎤</button>
            <div style={{ marginTop: 12, color: listening ? '#d32f2f' : '#555' }}>
              {listening ? t('voice.listening', '聆聽中…') : (busy ? t('voice.processing', '處理中…') : t('voice.tapToSpeak', '按下說話'))}
            </div>
            {(micPerm === 'denied' || micPerm === 'unsupported') && (
              <button onClick={() => setPermModalOpen(true)}
                style={{ marginTop: 10, background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
                {t('voice.micDenied.hint', '麥克風權限被拒，點此看如何開啟')}
              </button>
            )}
          </>
        ) : (
          <div style={{ color: '#ed6c02', marginBottom: 12 }}>{t('voice.noSupport', '您的瀏覽器不支援語音功能，請改用文字輸入')}</div>
        )}

        {/* 文字輸入（fallback，或不想說話時） */}
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <input style={{ flex: 1, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
            placeholder={t('voice.textInput', '或輸入文字指令')} value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { process(textInput); setTextInput(''); } }} />
          <button style={{ padding: '8px 16px', border: 'none', borderRadius: 4, background: '#1976d2', color: '#fff', cursor: 'pointer' }}
            disabled={busy} onClick={() => { process(textInput); setTextInput(''); }}>{t('common.send', '送出')}</button>
        </div>

        {heard && (
          <div style={{ marginTop: 24, textAlign: 'left' }}>
            <div style={{ color: '#999', fontSize: 13 }}>🗣️ {heard}</div>
            {answer && <div style={{ marginTop: 8, padding: 16, background: '#f5f5f5', borderRadius: 8, fontSize: 16 }}>{answer}</div>}
          </div>
        )}

        {!heard && (
          <div style={{ marginTop: 32, color: '#999', fontSize: 14, textAlign: 'left', lineHeight: 2 }}>
            <div>💡 {t('voice.calendarQueryPrompt', '試試說：今天／明天／這週的行程')}</div>
            <div>💡 {t('voice.contactLookupPrompt', '試試說：某某的電話')}</div>
            <div>💡 {t('voice.notificationReadPrompt', '試試說：唸最新通知')}</div>
          </div>
        )}
      </div>

      {/* 麥克風權限被拒 · 重置指引 modal（browser-specific） */}
      {permModalOpen && (
        <div role="dialog" aria-modal="true"
          onClick={() => setPermModalOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, maxWidth: 460, width: '100%', padding: 24,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)', textAlign: 'left' }}>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#d32f2f', marginBottom: 8 }}>
              ⚠️ {t('voice.micDenied.title', '麥克風權限被拒絕，無法使用語音功能')}
            </div>
            <div style={{ fontSize: 14, color: '#555', marginBottom: 12 }}>
              {t('voice.micDenied.intro', '請依你的瀏覽器開啟權限：')}
            </div>
            <div style={{ whiteSpace: 'pre-line', fontSize: 14, color: '#333', lineHeight: 1.9,
              background: '#f7f7f7', borderRadius: 6, padding: '12px 14px' }}>
              {t(`voice.micDenied.guide.${detectMicGuide()}`)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setPermModalOpen(false)}
                style={{ padding: '8px 16px', border: '1px solid #ccc', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                {t('voice.micDenied.cancel', '取消')}
              </button>
              <button onClick={onRetry}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 4, background: '#06c755', color: '#fff', cursor: 'pointer' }}>
                {t('voice.micDenied.retry', '重試')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoicePage;
